/**
 * Centralized Order Replacement Service
 *
 * Handles validation and router submission for all order replacement operations.
 * Ensures orders are only sent to router when:
 * 1. Operation is a legitimate drag (not click)
 * 2. Values have actually changed
 *
 * Single source of truth for order replacement logic across the application.
 */

import { hasOrderChanges, hasOTOPriceChanged } from "./orderConversions";

// Minimum drag distance in pixels to consider it a real drag (not accidental click)
const MIN_DRAG_DISTANCE = 5;

/**
 * Calculates the distance between two points
 * @param {Object} point1 - {x, y} coordinates
 * @param {Object} point2 - {x, y} coordinates
 * @returns {number} Distance in pixels
 */
const calculateDistance = (point1, point2) => {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Validates if a drag operation meets the criteria for order submission
 * @param {Object} options - Validation options
 * @param {Object} options.dragStart - Starting position {x, y}
 * @param {Object} options.dragEnd - Ending position {x, y}
 * @param {Object} options.originalOrder - Original order data
 * @param {Object} options.newValues - New values to compare
 * @param {string} options.operationType - Type of operation ('main-order', 'oto-order', 'modal')
 * @returns {Object} Validation result {isValid, reason, hasChanges, hasMinimumDrag}
 */
export const validateOrderReplacement = (options) => {
  const {
    dragStart,
    dragEnd,
    originalOrder,
    newValues,
    operationType = "unknown",
  } = options;

  console.log("🔍 OrderReplacementService: Validating replacement:", {
    operationType,
    dragStart,
    dragEnd,
    originalOrder: originalOrder
      ? { id: originalOrder.id, price: originalOrder.price }
      : null,
    newValues,
  });

  // For modal operations, we don't need drag distance validation
  const needsDragValidation = operationType !== "modal";

  let hasMinimumDrag = true;
  let dragDistance = 0;

  // Check minimum drag distance for interactive operations
  if (needsDragValidation && dragStart && dragEnd) {
    dragDistance = calculateDistance(dragStart, dragEnd);
    hasMinimumDrag = dragDistance >= MIN_DRAG_DISTANCE;

    console.log("📏 Drag distance check:", {
      distance: dragDistance,
      minimumRequired: MIN_DRAG_DISTANCE,
      hasMinimumDrag,
    });
  }

  // Check if values have actually changed
  let hasChanges = false;

  if (
    operationType === "oto-order" &&
    originalOrder &&
    newValues.price !== undefined
  ) {
    hasChanges = hasOTOPriceChanged(originalOrder, newValues.price);
  } else if (originalOrder && newValues) {
    hasChanges = hasOrderChanges(originalOrder, newValues);
  }

  console.log("🔄 Change detection result:", {
    hasChanges,
    operationType,
    originalValue:
      operationType === "oto-order" ? originalOrder?.price : "complex",
    newValue: operationType === "oto-order" ? newValues?.price : "complex",
  });

  // Determine if replacement is valid
  const isValid = hasMinimumDrag && hasChanges;

  let reason = "";
  if (!hasMinimumDrag) {
    reason = `Insufficient drag distance (${dragDistance.toFixed(1)}px < ${MIN_DRAG_DISTANCE}px)`;
  } else if (!hasChanges) {
    reason = "No changes detected in order values";
  } else {
    reason = "Valid replacement operation";
  }

  const result = {
    isValid,
    reason,
    hasChanges,
    hasMinimumDrag,
    dragDistance,
    operationType,
  };

  console.log("✅ OrderReplacementService validation result:", result);

  return result;
};

/**
 * Executes order replacement if validation passes
 * @param {Object} options - Replacement options
 * @param {Function} options.replacementFunction - Function to call if validation passes
 * @param {Object} options.validationOptions - Options for validateOrderReplacement
 * @returns {Promise<boolean>} true if replacement was executed, false if skipped
 */
export const executeOrderReplacement = async (options) => {
  const { replacementFunction, validationOptions } = options;

  if (!replacementFunction || typeof replacementFunction !== "function") {
    console.warn(
      "❌ OrderReplacementService: Invalid replacement function provided",
    );
    return false;
  }

  const validation = validateOrderReplacement(validationOptions);

  if (validation.isValid) {
    console.log(
      "🚀 OrderReplacementService: Executing replacement:",
      validation.reason,
    );

    try {
      await replacementFunction();
      console.log(
        "✅ OrderReplacementService: Replacement executed successfully",
      );
      return true;
    } catch (error) {
      console.error("❌ OrderReplacementService: Replacement failed:", error);
      throw error;
    }
  } else {
    console.log(
      "⏭️ OrderReplacementService: Skipping replacement:",
      validation.reason,
    );
    return false;
  }
};

/**
 * Creates a standardized drag tracking object for interactive components
 * @param {Object} startPosition - Starting mouse position {x, y}
 * @returns {Object} Drag tracker with start position and helper methods
 */
export const createDragTracker = (startPosition) => {
  return {
    startPosition,

    /**
     * Validates completion of drag operation
     * @param {Object} endPosition - Ending mouse position {x, y}
     * @param {Object} originalOrder - Original order data
     * @param {Object} newValues - New values after drag
     * @param {string} operationType - Type of operation
     * @returns {Object} Validation result
     */
    validateCompletion: (
      endPosition,
      originalOrder,
      newValues,
      operationType,
    ) => {
      return validateOrderReplacement({
        dragStart: startPosition,
        dragEnd: endPosition,
        originalOrder,
        newValues,
        operationType,
      });
    },
  };
};

/**
 * Service class for managing order replacements across the application
 */
export class OrderReplacementService {
  static instance = null;

  constructor() {
    if (OrderReplacementService.instance) {
      return OrderReplacementService.instance;
    }

    this.activeDrags = new Map(); // Track active drag operations
    OrderReplacementService.instance = this;
  }

  /**
   * Starts tracking a drag operation
   * @param {string} operationId - Unique ID for this drag operation
   * @param {Object} startPosition - Starting position {x, y}
   * @param {Object} originalOrder - Original order data
   */
  startDrag(operationId, startPosition, originalOrder) {
    this.activeDrags.set(operationId, {
      startPosition,
      originalOrder,
      startTime: Date.now(),
    });

    console.log("🎯 OrderReplacementService: Started tracking drag:", {
      operationId,
      startPosition,
    });
  }

  /**
   * Completes a drag operation with validation
   * @param {string} operationId - Unique ID for this drag operation
   * @param {Object} endPosition - Ending position {x, y}
   * @param {Object} newValues - New values after drag
   * @param {string} operationType - Type of operation
   * @param {Function} replacementFunction - Function to execute if valid
   * @returns {Promise<boolean>} true if replacement was executed
   */
  async completeDrag(
    operationId,
    endPosition,
    newValues,
    operationType,
    replacementFunction,
  ) {
    const dragData = this.activeDrags.get(operationId);

    // CRITICAL: Delete immediately to prevent race condition (Issue #122)
    // When chart is laggy, multiple completeDrag calls can fire in quick succession.
    // By deleting synchronously before any async work, we ensure only the first call proceeds.
    this.activeDrags.delete(operationId);

    if (!dragData) {
      console.warn(
        "❌ OrderReplacementService: No drag data found for operation:",
        operationId,
      );
      return false;
    }

    console.log(
      "🧹 OrderReplacementService: Processing drag completion for:",
      operationId,
    );

    const result = await executeOrderReplacement({
      replacementFunction,
      validationOptions: {
        dragStart: dragData.startPosition,
        dragEnd: endPosition,
        originalOrder: dragData.originalOrder,
        newValues,
        operationType,
      },
    });

    return result;
  }

  /**
   * Cancels a drag operation
   * @param {string} operationId - Unique ID for this drag operation
   */
  cancelDrag(operationId) {
    this.activeDrags.delete(operationId);
    console.log("❌ OrderReplacementService: Cancelled drag:", operationId);
  }
}

// Export singleton instance
export const orderReplacementService = new OrderReplacementService();
